import { UserFriendlyError } from '../../../../model/Error'

export type InteropUiErrorCode =
  | 'INTEROP_FORBIDDEN'
  | 'INTEROP_ORG_NOT_FOUND'
  | 'INTEROP_USER_NOT_FOUND'
  | 'INTEROP_INVALID_JSON'
  | 'INTEROP_VALIDATION_FAILED'
  | 'INTEROP_CODE_ALREADY_USED'
  | 'INTEROP_UNKNOWN_ERROR'

export interface InteropUiError {
  code: InteropUiErrorCode
  fieldErrors?: Record<string, string>
  rawStatus?: number
  rawMessage?: string
}

export class InteropUserFriendlyError extends UserFriendlyError {
  constructor(public readonly interopError: InteropUiError) {
    super('error', ...getUiMessageForCode(interopError.code))
  }
}

const getUiMessageForCode = (code: InteropUiErrorCode): [string, string] => {
  switch (code) {
    case 'INTEROP_FORBIDDEN':
      return ["Vous n'etes pas autorise a realiser cette action", 'Acces refuse']
    case 'INTEROP_ORG_NOT_FOUND':
      return ['Organisation introuvable', "L'organisation demandee n'existe pas"]
    case 'INTEROP_USER_NOT_FOUND':
      return ['Utilisateur introuvable', 'Un utilisateur reference est introuvable']
    case 'INTEROP_INVALID_JSON':
      return ['Contenu invalide', 'Le format de la requete est invalide']
    case 'INTEROP_VALIDATION_FAILED':
      return ['Le formulaire contient des erreurs', 'Corrigez les champs en erreur']
    case 'INTEROP_CODE_ALREADY_USED':
      return ['Code déjà utilisé', 'Ce code est déjà attribué à une autre organisation']
    default:
      return ['Une erreur est survenue', 'Veuillez reessayer ulterieurement']
  }
}

export const mapInteropPropertyToField = (property?: string): string => {
  if (!property) return 'form'
  return property.replace(/\[(\d+)\]/g, '.$1')
}

export const mapInteropApiErrorToUi = (apiResponse: any): InteropUiError => {
  const status = apiResponse?.status
  const message = apiResponse?.data?.message || apiResponse?.data?.detail || ''
  const violations = apiResponse?.data?.violations

  if (status === 403) return { code: 'INTEROP_FORBIDDEN', rawStatus: status, rawMessage: message }

  if (status === 404) {
    if (typeof message === 'string' && message.toLowerCase().includes('organization')) {
      return { code: 'INTEROP_ORG_NOT_FOUND', rawStatus: status, rawMessage: message }
    }
    return { code: 'INTEROP_USER_NOT_FOUND', rawStatus: status, rawMessage: message }
  }

  if (status === 409) {
    return {
      code: 'INTEROP_CODE_ALREADY_USED',
      fieldErrors: { code: 'Ce code est déjà utilisé par une autre organisation' },
      rawStatus: status,
      rawMessage: message,
    }
  }

  if (status === 400 && Array.isArray(violations) && violations.length > 0) {
    const fieldErrors: Record<string, string> = {}
    violations.forEach((violation) => {
      const key = mapInteropPropertyToField(violation?.propertyPath)
      fieldErrors[key] = violation?.message || 'Valeur invalide'
    })
    return {
      code: 'INTEROP_VALIDATION_FAILED',
      fieldErrors,
      rawStatus: status,
      rawMessage: message,
    }
  }

  if (status === 400 && typeof message === 'string' && message.startsWith('Invalid JSON body')) {
    return { code: 'INTEROP_INVALID_JSON', rawStatus: status, rawMessage: message }
  }

  return { code: 'INTEROP_UNKNOWN_ERROR', rawStatus: status, rawMessage: message }
}
